import { State } from "country-state-city";
import type { AddressProfile, RegionOption } from "@/interfaces/redemption";

/**
 * Per-country address rules for the recipient-details form (RFW-7).
 *
 * Owns only the *field requirements* — which admin-area field a country uses
 * (State / Province / County / Region, or none), whether it's a dropdown, and the
 * postal label + presence. The actual region OPTIONS (states/provinces + their
 * abbreviations) come from the shared `country-state-city` dataset via
 * `getRegionOptions`, so we don't hand-maintain state lists. A pragmatic profiles
 * model covering the common shipping destinations with a sensible international default.
 * The `AddressProfile` / `RegionOption` shapes live in `interfaces/redemption`.
 */

const US_PROFILE: AddressProfile = {
  key: "US",
  regionLabel: "State",
  regionSelect: true,
  regionRequired: true,
  postalLabel: "ZIP code",
  postalRequired: true,
};
const CA_PROFILE: AddressProfile = {
  key: "CA",
  regionLabel: "Province",
  regionSelect: true,
  regionRequired: true,
  postalLabel: "Postal code",
  postalRequired: true,
};
const AU_PROFILE: AddressProfile = {
  key: "AU",
  regionLabel: "State / Territory",
  regionSelect: true,
  regionRequired: true,
  postalLabel: "Postcode",
  postalRequired: true,
};
const UK_PROFILE: AddressProfile = {
  key: "GB",
  regionLabel: "County",
  regionSelect: false,
  regionRequired: false,
  postalLabel: "Postcode",
  postalRequired: true,
};
const INTL_PROFILE: AddressProfile = {
  key: "INTL",
  regionLabel: "Region / Province",
  regionSelect: false,
  regionRequired: false,
  postalLabel: "Postal code",
  postalRequired: true,
};
const INTL_NO_POSTAL_PROFILE: AddressProfile = {
  key: "INTL_NP",
  regionLabel: "Region / Province",
  regionSelect: false,
  regionRequired: false,
  postalLabel: null,
  postalRequired: false,
};
// Most continental-Europe (+ EEA) addresses: no region component, and the
// postal code precedes the city ("10115 Berlin").
const EU_PROFILE: AddressProfile = {
  key: "EU",
  regionLabel: null,
  regionSelect: false,
  regionRequired: false,
  postalLabel: "Postal code",
  postalRequired: true,
  postalBeforeCity: true,
};
// Italy uses a province (2-letter code, e.g. "RM") alongside the postal code.
const IT_PROFILE: AddressProfile = {
  key: "IT",
  regionLabel: "Province",
  regionSelect: false,
  regionRequired: false,
  postalLabel: "Postal code",
  postalRequired: true,
};
// Ireland: County + optional Eircode (not a no-postal country).
const IE_PROFILE: AddressProfile = {
  key: "IE",
  regionLabel: "County",
  regionSelect: false,
  regionRequired: false,
  postalLabel: "Eircode",
  postalRequired: false,
};

const BESPOKE: Record<string, AddressProfile> = {
  US: US_PROFILE,
  CA: CA_PROFILE,
  AU: AU_PROFILE,
  GB: UK_PROFILE,
  IT: IT_PROFILE,
  IE: IE_PROFILE,
};
/** Continental-Europe + EEA countries that use the no-region EU layout. */
const EU_LAYOUT = new Set([
  "AT",
  "BE",
  "CZ",
  "DK",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IS",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "ES",
  "SE",
  "CH",
]);
/** Common destinations without postal codes (among the shipping countries). */
const NO_POSTAL = new Set(["HK", "AE", "QA"]);

/** Resolve a country ISO-2 code to its address profile. */
export function getAddressProfile(code: string): AddressProfile {
  if (BESPOKE[code]) return BESPOKE[code];
  if (EU_LAYOUT.has(code)) return EU_PROFILE;
  if (NO_POSTAL.has(code)) return INTL_NO_POSTAL_PROFILE;
  return INTL_PROFILE;
}

/** Region (state/province) options for a country's dropdown, from `country-state-city`. */
export function getRegionOptions(code: string): RegionOption[] {
  return State.getStatesOfCountry(code).map((s) => ({
    value: s.name,
    label: s.name,
    abbr: s.isoCode,
  }));
}

/** Short form of a region value for the confirmation review (falls back to raw). */
export function regionAbbr(regions: RegionOption[], value: string): string {
  return regions.find((o) => o.value === value)?.abbr ?? value;
}

/**
 * Resolve an incoming region string (Google/Smarty give the short code, e.g. "CA")
 * to the option `value` the region <select> is keyed by (its full name, e.g.
 * "California") so an auto-filled address selects the right option instead of
 * leaving the field blank. Matches by abbreviation first, then by name; passes the
 * input through unchanged when there's no options list (free-text region countries).
 */
export function matchRegionValue(regions: RegionOption[], input: string): string {
  if (!input || regions.length === 0) return input;
  const q = input.trim().toLowerCase();
  const hit =
    regions.find((o) => o.abbr.toLowerCase() === q) ??
    regions.find((o) => o.value.toLowerCase() === q);
  return hit ? hit.value : input;
}
